import { Workflow, WorkflowNode } from '../api/model.ts';
import { FaFileCsv, FaFileExcel, FaFileZipper, FaFileCode, FaFile, FaFileWord, FaFileImage } from 'react-icons/fa6';
import { detectLanguage } from './langDetector.ts';

const CSV_FILE_TYPES = ['text/csv'];
const EXCEL_FILE_TYPES = [
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];
const DOC_FILE_TYPES = ['application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
const RTF_FILE_TYPES = ['application/rtf', 'text/rtf'];
const ARCHIVE_FILE_TYPES = [
  'application/zip',
  'application/x-zip-compressed',
  'application/tar',
  'application/x-tar',
  'application/gzip',
  'application/x-gzip',
  'application/x-bzip2',
  'application/x-xz',
];
const JSON_FILE_TYPES = ['application/json'];

function formatBytes(bytes: number, decimals = 2) {
  if (!+bytes) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
}

function dataURLtoFile(dataUrl: string) {
  const arr = dataUrl.split(','),
    mime = /:(.*?);/.exec(arr[0])![1],
    filename = /name=(.*?);/.exec(arr[0])![1],
    bstr = atob(arr[1]),
    n = bstr.length,
    u8arr = new Uint8Array(n);

  let index = n;
  while (index--) {
    u8arr[index] = bstr.charCodeAt(index);
  }

  return new File([u8arr], filename, { type: mime });
}

const JSONToFile = (obj: object, filename: string) => {
  const blob = new Blob([JSON.stringify(obj, null, 2)], {
    type: 'application/json',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}.json`;
  a.click();
  URL.revokeObjectURL(url);
};

export type ParseResult = {
  content: string;
  language: string;
};

const convertPythonToJSON = (str: string): string => {
  return str
    .replace(/\bNone\b/g, 'null')
    .replace(/\bTrue\b/g, 'true')
    .replace(/\bFalse\b/g, 'false')
    .replace(/'/g, '"');
};

const parseOutputCellContent = (data: string): ParseResult => {
  const language = detectLanguage(data.slice(0, 4096));
  let parsedData = null;
  try {
    parsedData = JSON.parse(data.trim()) as object;
  } catch {
    try {
      parsedData = JSON.parse(convertPythonToJSON(data.trim())) as object;
    } catch {
      // not JSON or Python dict
    }
  }

  return { content: parsedData ? JSON.stringify(parsedData, null, 2) : data, language };
};

export const toLocaleDateStr = (date: string | undefined | null): string | null => {
  try {
    if (!date) {
      return null;
    }
    return new Date(date).toLocaleString();
  } catch {
    return null;
  }
};

export const formatDuration = (milliseconds: number | null | undefined): string => {
  if (!milliseconds) {
    return '0s';
  }
  const totalSeconds = Math.floor(milliseconds / 1000);
  const hrs = Math.floor(totalSeconds / 3600);
  const mins = Math.floor((totalSeconds % 3600) / 60);
  const secs = Math.floor(totalSeconds % 60);

  if (hrs > 0) {
    return `${hrs}h ${mins}m ${secs}s`;
  } else if (mins > 0) {
    return `${mins}m ${secs}s`;
  } else {
    return milliseconds > 0 && totalSeconds < 1 ? `<1s` : `${secs}s`;
  }
};

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

export const formatDateTimeCompact = (value: string | null | undefined, now: Date = new Date()): string | null => {
  if (!value) return null;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  const time = date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  const isToday = date.toDateString() === now.toDateString();
  return isToday ? time : `${date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}, ${time}`;
};

export const formatTimeAgo = (value: string | null | undefined, now: Date = new Date()): string => {
  if (!value) return 'never';

  const elapsed = now.getTime() - new Date(value).getTime();
  if (Number.isNaN(elapsed)) return 'unknown';
  if (elapsed < MINUTE_MS) return 'just now';
  if (elapsed < HOUR_MS) return `${Math.floor(elapsed / MINUTE_MS)}m ago`;
  if (elapsed < DAY_MS) return `${Math.floor(elapsed / HOUR_MS)}h ago`;

  const days = Math.floor(elapsed / DAY_MS);
  return days < 30 ? `${days}d ago` : (toLocaleDateStr(value) ?? 'unknown');
};

export const simplifiedMimeType = (mimeType: string): string => {
  return mimeType.startsWith('text/csv') ? 'text/csv' : mimeType;
};

export const downloadBlob = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);

  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();

  URL.revokeObjectURL(url);
  document.body.removeChild(anchor);
};

export const washedOutColor = (color: string) =>
  `rgba(${parseInt(color.slice(1, 3), 16)}, ${parseInt(
    color.slice(3, 5),
    16,
  )}, ${parseInt(color.slice(5, 7), 16)}, 0.4)`;

const getWorkflowInputOutputNodes = (workflow: Workflow): { inputs: WorkflowNode[]; outputs: WorkflowNode[] } => {
  const inputs: WorkflowNode[] = [];
  const outputs: WorkflowNode[] = [];
  const sourceIds = new Set(workflow.edges.map((edge) => edge.source));
  const targetIds = new Set(workflow.edges.map((edge) => edge.target));
  workflow.nodes.forEach((node) => {
    if (!sourceIds.has(node.id)) {
      outputs.push(node);
    }
    if (!targetIds.has(node.id)) {
      inputs.push(node);
    }
  });
  return {
    inputs,
    outputs,
  };
};

const findRevisedWorkflow = (revisedWorkflows: Workflow[], id: string, revision: number) =>
  revisedWorkflows.find((w) => w.id === id && w.revision === revision);

const getFileIcon = (mimeType: string, fileName: string, size = 32) => {
  const iconProps = { size, className: 'text-gray-400 group-hover:text-blue-500 transition-colors duration-300' };

  if (mimeType.includes('text/csv')) {
    return (
      <FaFileCsv {...iconProps} className="text-green-500 group-hover:text-green-600 transition-colors duration-300" />
    );
  }
  if (
    mimeType.includes('application/vnd.ms-excel') ||
    mimeType.includes('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')
  ) {
    return (
      <FaFileExcel
        {...iconProps}
        className="text-green-600 group-hover:text-green-700 transition-colors duration-300"
      />
    );
  }
  if (
    mimeType.includes('application/vnd.openxmlformats-officedocument.wordprocessingml.document') ||
    mimeType.includes('text/rtf') ||
    mimeType.includes('application/rtf')
  ) {
    return (
      <FaFileWord {...iconProps} className="text-blue-600 group-hover:text-blue-700 transition-colors duration-300" />
    );
  }
  if (
    mimeType.includes('application/zip') ||
    mimeType.includes('application/tar') ||
    mimeType.includes('application/x-tar') ||
    mimeType.includes('application/gzip') ||
    mimeType.includes('application/x-gzip') ||
    mimeType.includes('application/x-zip-compressed') ||
    mimeType.includes('application/x-bzip2') ||
    mimeType.includes('application/x-xz')
  ) {
    return (
      <FaFileZipper
        {...iconProps}
        className="text-orange-500 group-hover:text-orange-600 transition-colors duration-300"
      />
    );
  }
  if (
    mimeType.includes('text/plain') ||
    mimeType.includes('application/json') ||
    mimeType.includes('application/javascript') ||
    mimeType.includes('text/html')
  ) {
    return (
      <FaFileCode
        {...iconProps}
        className="text-yellow-200 group-hover:text-yellow-300 transition-colors duration-300"
      />
    );
  }
  if (mimeType.startsWith('image/')) {
    return (
      <FaFileImage
        {...iconProps}
        className="text-purple-500 group-hover:text-purple-600 transition-colors duration-300"
      />
    );
  }

  const extension = fileName.split('.').pop()?.toLowerCase();
  if (extension) {
    switch (extension) {
      case 'csv':
        return (
          <FaFileCsv
            {...iconProps}
            className="text-green-500 group-hover:text-green-600 transition-colors duration-300"
          />
        );
      case 'xlsx':
      case 'xls':
        return (
          <FaFileExcel
            {...iconProps}
            className="text-green-600 group-hover:text-green-700 transition-colors duration-300"
          />
        );
      case 'docx':
      case 'rtf':
        return (
          <FaFileWord
            {...iconProps}
            className="text-blue-600 group-hover:text-blue-700 transition-colors duration-300"
          />
        );
      case 'zip':
      case 'tar':
      case 'gz':
      case 'rar':
      case 'bz2':
      case 'xz':
        return (
          <FaFileZipper
            {...iconProps}
            className="text-orange-500 group-hover:text-orange-600 transition-colors duration-300"
          />
        );
      case 'txt':
      case 'json':
      case 'js':
      case 'ts':
      case 'py':
      case 'java':
      case 'cpp':
      case 'c':
        return (
          <FaFileCode
            {...iconProps}
            className="text-gray-600 group-hover:text-gray-700 transition-colors duration-300"
          />
        );
      case 'png':
      case 'jpg':
      case 'jpeg':
      case 'svg':
      case 'gif':
      case 'bmp':
      case 'tiff':
        return (
          <FaFileImage
            {...iconProps}
            className="text-purple-500 group-hover:text-purple-600 transition-colors duration-300"
          />
        );
      default:
        return <FaFile {...iconProps} />;
    }
  }

  return <FaFile {...iconProps} />;
};

const isAnyInputFocused = () => {
  const active = document.activeElement;
  return active?.tagName === 'INPUT' || active?.tagName === 'TEXTAREA' || active?.tagName === 'SELECT';
};

export {
  formatBytes,
  dataURLtoFile,
  JSONToFile,
  parseOutputCellContent,
  getWorkflowInputOutputNodes,
  findRevisedWorkflow,
  getFileIcon,
  isAnyInputFocused,
  CSV_FILE_TYPES,
  EXCEL_FILE_TYPES,
  DOC_FILE_TYPES,
  RTF_FILE_TYPES,
  ARCHIVE_FILE_TYPES,
  JSON_FILE_TYPES,
};
